import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Divider, Paper, Stack, Typography } from '@mui/material'

import {
  AVG_DECREASE_INFO,
  AVG_INCREASE_INFO,
  COLOR_TILE_AVG_DECREASE,
  COLOR_TILE_AVG_INCREASE,
  COLOR_TILE_DECREASED,
  COLOR_TILE_INCREASED,
  COLOR_TILE_NO_CHANGE,
  DECREASED_INFO,
  INCREASED_INFO,
  NEGOTIATED_DOWN_INFO,
  NEGOTIATED_UP_INFO,
  NO_CHANGE_INFO,
} from 'components/reports/Metrics/constants/NegotiationOverview'
import { BidNegotiationOverviewDto } from 'generated/graphql'
import { formatGbp, shareOfNegotiated } from 'helpers/negotiationOutcomeTiles.helpers'

import { NegotiationOutcomeTile } from './NegotiationOutcomeTile'

interface Props {
  data?: BidNegotiationOverviewDto | null
  loading?: boolean
}

export const NegotiationOutcomeTiles = ({ data, loading }: Props) => (
  <Box>
    <Paper variant='outlined' sx={{ borderRadius: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        divider={<Divider orientation='vertical' flexItem />}
        justifyContent='space-around'
        alignItems={{ xs: 'stretch', md: 'center' }}
        useFlexGap
        flexWrap='wrap'>
        <NegotiationOutcomeTile
          color={COLOR_TILE_DECREASED}
          direction='down'
          caption='Decreased price'
          info={DECREASED_INFO}
          value={data ? data.decreasedJobs.toLocaleString() : null}
          valueSuffix={data ? shareOfNegotiated(data.decreasedJobs, data.negotiatedJobs) : null}
          subtitle='Negotiated for less'
          loading={loading}
        />
        <NegotiationOutcomeTile
          color={COLOR_TILE_NO_CHANGE}
          direction='none'
          caption='No change'
          info={NO_CHANGE_INFO}
          value={data ? data.noChangeJobs.toLocaleString() : null}
          valueSuffix={data ? shareOfNegotiated(data.noChangeJobs, data.negotiatedJobs) : null}
          subtitle='No price change'
          loading={loading}
        />
        <NegotiationOutcomeTile
          color={COLOR_TILE_INCREASED}
          direction='up'
          caption='Increased price'
          info={INCREASED_INFO}
          value={data ? data.increasedJobs.toLocaleString() : null}
          valueSuffix={data ? shareOfNegotiated(data.increasedJobs, data.negotiatedJobs) : null}
          subtitle='Negotiated for more'
          loading={loading}
        />
        <NegotiationOutcomeTile
          color={COLOR_TILE_AVG_DECREASE}
          direction='down'
          caption='Avg. decrease'
          info={AVG_DECREASE_INFO}
          value={
            data?.averageDecreasePercentage != null
              ? `${data.averageDecreasePercentage.toLocaleString()}%`
              : null
          }
          subtitle='Average decrease'
          loading={loading}
        />
        <NegotiationOutcomeTile
          color={COLOR_TILE_AVG_INCREASE}
          direction='up'
          caption='Avg. increase'
          info={AVG_INCREASE_INFO}
          value={
            data?.averageIncreasePercentage != null
              ? `+${data.averageIncreasePercentage.toLocaleString()}%`
              : null
          }
          subtitle='Average increase'
          loading={loading}
        />
      </Stack>
    </Paper>

    {/* The monetary strip is not in the mock-up: AC4 requires a monetary negotiation value,
        and the PO chose separate down/up totals over a single net figure. */}
    <Paper variant='outlined' sx={{ borderRadius: 2, mt: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        divider={<Divider orientation='vertical' flexItem />}
        justifyContent='space-around'
        alignItems={{ xs: 'stretch', sm: 'center' }}>
        <NegotiationOutcomeTile
          color={COLOR_TILE_AVG_DECREASE}
          direction='down'
          caption='Negotiated down'
          info={NEGOTIATED_DOWN_INFO}
          value={data ? formatGbp(data.totalNegotiatedDown) : null}
          subtitle='Total reduced (GBP)'
          loading={loading}
        />
        <NegotiationOutcomeTile
          color={COLOR_TILE_AVG_INCREASE}
          direction='up'
          caption='Negotiated up'
          info={NEGOTIATED_UP_INFO}
          value={data ? formatGbp(data.totalNegotiatedUp) : null}
          subtitle='Total added (GBP)'
          loading={loading}
        />
      </Stack>
    </Paper>

    <Stack direction='row' spacing={0.75} alignItems='center' mt={1}>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      <Typography variant='caption' color='text.secondary'>
        Totals may not add up to 100% due to rounding.
        {data != null &&
          data.monetaryExcludedJobs + data.nonComparableJobs > 0 &&
          ` ${(data.monetaryExcludedJobs + data.nonComparableJobs).toLocaleString()} negotiated ` +
            'job(s) are excluded from the figures above because their prices could not be ' +
            'compared or converted to GBP.'}
      </Typography>
    </Stack>
  </Box>
)
